import { Card, CardContent, CardHeader, Grid, Skeleton, Typography } from '@mui/material';
import { apiEntities, generic } from 'apis';
import Questionnaire from 'common/Questionnaire';
import { FileProvider, useFileContext } from 'context/FilesV2';
import { formatFiles, downloadFile, uploadFiles, deleteFile, deleteBundle } from 'utils';
import { FunctionsReverse, ProjectTables } from 'enums';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FileGroup } from 'common/FileUpload';

const FilesUpload = forwardRef(({ fileGroups, id, functionId }, ref) => {
    const { fileGroups: fileGroupsV2, control: filesControl } = useFileContext();

    useImperativeHandle(ref, () => ({
        uploadFiles: async () => {
            await filesControl.uploadFileGroups();
        },
        filebundles: {
            files: fileGroupsV2?.flatMap((group) => group?.bundles),
            personFunctionId: id,
            functionId: functionId
        }
    }));

    useEffect(() => {
        try {
            const formattedFiles = formatFiles(fileGroups);
            if (formattedFiles !== undefined) {
                filesControl.initialize(formattedFiles);
            } else {
                filesControl.initialize([]);
            }
        } catch (error) {
            console.error('error', error);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileGroups]);
    return (
        <>
            {fileGroupsV2?.map((group, index) => (
                <FileGroup
                    key={group.id}
                    id={group.id}
                    title={group?.title}
                    maxSize={2097152}
                    maxFiles={5}
                    maxBundles={1}
                    fields={group.fields}
                    bundles={group?.bundles}
                    templates={group?.templates}
                />
            ))}
        </>
    );
});

const FilesPart = ({ func, functionsQuery }, ref) => {
    const { t } = useTranslation();
    const { id, functionId } = func;

    const filesQuery = useQuery(['files', id, functionId], async ({ queryKey }) => {
        const [, id] = queryKey;

        const getFilesRequest = {
            DataKey: 'FunctionId',
            DataValue: functionId,
            RecordId: id,
            RecordGuid: null,
            ProjectTableId: ProjectTables.PersonFunction
        };
        const files = await generic.getFiles(getFilesRequest);
        return files;
    });
    if (functionsQuery?.data?.map((f) => f.value).includes(func?.functionId))
        return {
            loading: <Skeleton />,
            success: !filesQuery?.data ? null : (
                <Card elevation={3} sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid item xs={12} marginBottom={3}>
                            <Typography color="primary" variant="h4">
                                {FunctionsReverse?.[func?.functionId] ?? func?.functionId} {t('Profile.Files')}
                            </Typography>
                        </Grid>

                        <FileProvider
                            downloadFile={(fileId, type) => downloadFile(fileId, type)}
                            uploadFiles={(fileGroups) => uploadFiles(fileGroups, null)}
                            onDeleteBundle={async (bundleId) =>
                                await deleteBundle(bundleId, ProjectTables.Application, null, id === '0' ? null : id, null)
                            }
                            onDeleteFile={async (fileId) =>
                                await deleteFile(fileId, ProjectTables.Application, null, id === '0' ? null : id, null)
                            }
                        >
                            <FilesUpload fileGroups={filesQuery?.data} ref={ref} functionId={functionId} id={id} />
                        </FileProvider>
                    </CardContent>
                </Card>
            ),
            error: <>Error fetching required attachments</>
        }[filesQuery?.status];
};

// const FunctionQuestions = ({ functionId, qst }) => {
//     const form = useFormContext();
//     const { fields, append, prepend, remove, swap, move, insert, replace } = useFieldArray({
//         control: form.control,
//         name: `questionnaire${functionId}`
//     });

//     return <Questionnaire flatQuestions data={qst} append={append} fields={fields} name={`questionnaire${functionId}`} />;
// };

export default forwardRef(FilesPart);
